import clsx from 'clsx'
import { FC } from 'react'
import * as Yup from 'yup'
import { FastField, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { createSearchParams, useNavigate } from 'react-router-dom'

import FormikInput from '../FormikInput'
import { useMutation, useQuery } from 'react-query'
import placeService from '../../services/placeService'
import { useUser } from '../../hooks/useUser'
import NewLocationBtn from '../NewLocationBtn'
import { queryClient } from '../../utils/react-query'
import toast from 'react-hot-toast'
import { BuzzlUsersError } from '../../types/buzzl-user'

interface Search {
  search: string
}

interface LocationHeaderProps {
  urlParams: any
  filterPending: () => void
  filterBtnAction?: () => void
  nbOfExtraFilter?: number
}

const schema = Yup.object().shape({
  search: Yup.string(),
})

const LocationHeader: FC<LocationHeaderProps> = ({
  urlParams,
  filterBtnAction,
  nbOfExtraFilter = 0,
  filterPending,
}) => {
  const navigate = useNavigate()
  const user = useUser()
  const pendingClaims = useQuery(
    ['pending-locations'],
    () =>
      placeService.count({
        claimStatus: 'PENDING',
      }),
    {
      enabled: user.data?.type === 'BUZZL', //its not needed on customers view
    }
  )
  const requestClaimPlace = useMutation(placeService.requestClaimPlace, {
    onSuccess: async () => {
      queryClient.refetchQueries(['locations'])
      toast.success('Location Submitted.')
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const initialValues: Search = {
    search: urlParams.search || '',
  }

  const handleOnSubmit = (values: Search, formikBag: FormikHelpers<Search>) => {
    if (urlParams.search !== values.search) {
      navigate({
        search: `?${createSearchParams({
          search: values.search,
        })}`,
      })
    }

    formikBag.resetForm()
  }

  return (
    <div className="mb-10 flex flex-wrap justify-between ">
      <div className="flex items-center">
        <h1 className="page-title">Locations</h1>
      </div>
      <div className="flex flex-row items-center space-x-3">
        {/*Display it only if there is at least one pending claim*/}
        {pendingClaims.status === 'success' && pendingClaims.data ? (
          <div>
            <button
              onClick={filterPending}
              className="inline-flex items-center rounded-md bg-yellow-100 px-2.5 py-0.5 text-sm font-medium text-yellow-800"
            >
              <svg className="-ml-0.5 mr-1.5 h-2 w-2 text-yellow-400" fill="currentColor" viewBox="0 0 8 8">
                <circle cx={4} cy={4} r={3} />
              </svg>
              Claims Pending
            </button>
          </div>
        ) : null}

        {user.data?.type === 'CUSTOMER' && user.data?.role !== 'OPERATOR' ? (
          <NewLocationBtn
            type="button"
            className="btn btn-primary"
            nbRemainingLicenses={user.data?.customerUser?.customer?.nbRemainingLicenses || 0}
            claimFn={async (place) => {
              await requestClaimPlace.mutate({ googlePlaceId: place.googlePlaceId })
            }}
          >
            Request to Claim
          </NewLocationBtn>
        ) : null}

        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={schema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleOnSubmit}
        >
          {({ isSubmitting }: FormikProps<Search>) => (
            <Form className="space-y-6" noValidate>
              <fieldset disabled={isSubmitting}>
                <div className="space-y-3">
                  <FastField type="search" name="search" component={FormikInput} placeholder="Search Location Name" />
                </div>
              </fieldset>
            </Form>
          )}
        </Formik>

        {filterBtnAction && (
          <button
            onClick={filterBtnAction}
            className={clsx({
              'btn btn-white': true,
              'btn-filter after:content-[attr(data-filter)]': urlParams.counter + nbOfExtraFilter,
            })}
            data-filter={urlParams.counter + nbOfExtraFilter}
          >
            Filter
          </button>
        )}
      </div>
    </div>
  )
}

export default LocationHeader
